import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import React, { useMemo, useState } from "react";
import { CATEGORY_COLORS, DEFAULT_CATEGORY_COLOR, getPrimaryCategory, isArchivedDashboard, SearchableDashboard } from "../../../constants";
import {
  useMyDashboardAccessRequests,
  useSubmitDashboardAccessRequest,
} from "../../../hooks/react-query/access-requests/useMyDashboardAccessRequests";
import { CollapsibleCategorySection } from "./CollapsibleCategorySection";

interface Props {
  dashboards: SearchableDashboard[];
}

// GET /dashboards now returns every dashboard with a per-caller `has_access` flag computed
// server-side (docs/needed-endpoints.md §1) - this used to re-derive access client-side via
// PrivateRoute's own accessControl.ts, which only worked because the mock registry was
// unfiltered; the real endpoint's `has_access` is the actual source of truth now, so that
// duplicate computation is gone.
export const DashboardAccessSection = (props: Props) => {
  const { dashboards } = props;
  const { myRequests } = useMyDashboardAccessRequests();
  const submitMutation = useSubmitDashboardAccessRequest();

  // Sorted by category then name - dashboards.filter(...) already returns a fresh array, so
  // .sort() here is safe (doesn't mutate the dashboards prop itself). Archived dashboards (either
  // sense - see isArchivedDashboard) are excluded too - requesting access to one would be
  // pointless, since it belongs only on the dedicated /archived page regardless of has_access.
  const inaccessibleDashboards = useMemo(
    () =>
      dashboards
        .filter((dashboard) => !dashboard.has_access && !isArchivedDashboard(dashboard))
        .sort((a, b) => getPrimaryCategory(a).localeCompare(getPrimaryCategory(b)) || a.name.localeCompare(b.name)),
    [dashboards]
  );

  // Grouped by category for the collapsible sections below - `inaccessibleDashboards` is already
  // sorted by category first, so a Set built from it (which preserves insertion order) naturally
  // yields categories in the same alphabetical order too, with no separate sort needed here.
  const groupedByCategory = useMemo(() => {
    const categories = Array.from(new Set(inaccessibleDashboards.map((dashboard) => getPrimaryCategory(dashboard))));
    return categories.map((category) => ({
      category,
      dashboards: inaccessibleDashboards.filter((dashboard) => getPrimaryCategory(dashboard) === category),
    }));
  }, [inaccessibleDashboards]);

  // Categories default open - similar to Dashboards page's own collapsible groups (see
  // CollapsibleDashboardGroup.tsx), but each category here is independently toggleable rather than
  // a flat sorted list, so a user with access gaps across many categories can collapse the ones
  // they don't care about right now.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (category: string) => setExpandedGroups((prev) => ({ ...prev, [category]: !(prev[category] ?? true) }));

  const handleRequestAccess = (dashboard: SearchableDashboard) => {
    submitMutation.mutate(dashboard.id);
  };

  if (inaccessibleDashboards.length === 0) {
    return (
      <div className="settings_page_access">
        <div className="settings_page_section_header">
          <LockOutlinedIcon fontSize="small" />
          <h2 className="settings_page_section_title">My Dashboard Access</h2>
        </div>
        <div className="settings_page_section_divider" />
        <p className="settings_access_empty">You already have access to every dashboard.</p>
      </div>
    );
  }

  return (
    <div className="settings_page_access">
      <div className="settings_page_section_header">
        <LockOutlinedIcon fontSize="small" />
        <h2 className="settings_page_section_title">My Dashboard Access</h2>
      </div>
      <div className="settings_page_section_divider" />
      {groupedByCategory.map((group) => (
        <CollapsibleCategorySection
          key={group.category}
          label={
            <>
              {/* Same color source/markup as Settings' own Engagement section right above this one
                  (SettingsPage.tsx) and DashboardCard's pill - CATEGORY_COLORS, keyed by category
                  name, so a category reads as the same color everywhere it appears. */}
              <span
                className="settings_engagement_group_dot"
                style={{ backgroundColor: CATEGORY_COLORS[group.category] ?? DEFAULT_CATEGORY_COLOR }}
              />
              {group.category}
            </>
          }
          testKey={group.category}
          count={group.dashboards.length}
          isOpen={expandedGroups[group.category] ?? true}
          onToggle={() => toggleGroup(group.category)}
        >
          <div className="settings_access_list">
            {group.dashboards.map((dashboard) => {
              const pending = myRequests.some((request) => request.dashboard_id === dashboard.id && request.status === "pending");
              return (
                <div className="settings_access_row" key={dashboard.key} data-test-id={`dashboard-access-row-${dashboard.key}`}>
                  <div className="settings_access_row_details">
                    <p className="settings_access_row_name">{dashboard.name}</p>
                  </div>
                  <button
                    className="settings_button_secondary"
                    onClick={() => handleRequestAccess(dashboard)}
                    disabled={pending}
                    data-test-id={`dashboard-access-request-${dashboard.key}`}
                  >
                    {pending ? "Access requested" : "Request access"}
                  </button>
                </div>
              );
            })}
          </div>
        </CollapsibleCategorySection>
      ))}
    </div>
  );
};
